// How a lead's metadata reads on screen.
//
// Metadata is stored as space-free codes ("CurrentMember", "FreeTrialBooked"); these turn
// them into the labels the CRM shows. A leaf module with no component imports, so the table
// and the lead detail panel share one vocabulary — they render the same lead side by side,
// and two copies of these maps would eventually disagree.

import { MemberProfile } from "../../../model/members/MemberProfile";
import { MemberWithPrimaryFollowup } from "../../../utils/memberFollowupUtils";

/**
 * A CRM row: a lead with its primary follow-up, plus the manager's instruction from the
 * batch of calls it was assigned with. The note is read per-load by a separate query rather
 * than joined onto the leads themselves, so it's grafted on here rather than living on
 * `MemberWithPrimaryFollowup`, which is shared well beyond this screen.
 */
export type LeadRow = MemberWithPrimaryFollowup & { assignmentNote?: string };

export const LEAD_SOURCE_LABELS: Record<string, string> = {
  Visitor: "Visitor",
  Tour: "Tour",
  Call: "Call",
  CurrentMember: "Current Member",
  PreviousMember: "Previous Member",
  LegacyMember: "Legacy Member",
  Member: "Member",
};

export const LEAD_SOURCE_COLORS: Record<string, string> = {
  Visitor: "blue",
  Tour: "volcano",
  Call: "gold",
  "Current Member": "green",
  "Previous Member": "orange",
  "Legacy Member": "purple",
  Member: "cyan",
};

export const LEAD_TYPE_LABELS: Record<string, string> = {
  ColdLead: "Cold Lead",
  WarmLead: "Warm Lead",
  FreeTrialBooked: "Free Trial Booked",
  TourBooked: "Tour Booked",
  Membership: "Membership",
  DeadLead: "Dead Lead",
};

export const LEAD_TYPE_COLORS: Record<string, string> = {
  "Dead Lead": "default",
  "Cold Lead": "blue",
  "Warm Lead": "orange",
  "Free Trial Booked": "geekblue",
  "Tour Booked": "purple",
  Membership: "green",
};

/**
 * Fallback labels for HowTheyHeard codes that the client's configured list doesn't cover.
 *
 * The configured `signup.howTheyHeard` list is the source of truth (FORT-349) — this map only catches
 * codes a client has since removed from it, most notably 'Member', which was retired in favour of
 * 'FriendFamily' but still keys plenty of historic member rows. Without it those leads would render a
 * bare code in the CRM.
 */
export const HOW_THEY_HEARD_FALLBACK_LABELS: Record<string, string> = {
  WalkingBy: "Walking by",
  SocialMedia: "Social media",
  WebsiteGoogle: "Website",
  FriendFamily: "Referred by a friend",
  Member: "Referred by a member",
};

/** Looks a metadata code up in its label map, tolerating values already stored with spaces. */
export function labelFor(raw: unknown, labels: Record<string, string>): string {
  if (typeof raw !== "string" || !raw) return "";
  const code = raw.replace(/\s+/g, "");
  return labels[code] ?? raw;
}

export const leadTypeLabel = (member: MemberProfile): string =>
  labelFor(member.metadata?.LeadType, LEAD_TYPE_LABELS);

export const leadSourceLabel = (member: MemberProfile): string =>
  labelFor(member.metadata?.LeadSource, LEAD_SOURCE_LABELS);

/**
 * Label a member's HowTheyHeard code, preferring the client's configured wording.
 *
 * `options` comes from `signup.howTheyHeard` (via `useClientConfig`) so the CRM and the retention
 * report agree on what staff see; anything the client has dropped from that list falls back to
 * {@link HOW_THEY_HEARD_FALLBACK_LABELS}, then to the raw code.
 */
export const howTheyHeardLabel = (
  member: MemberProfile,
  options?: readonly { value: string; label: string }[] | null
): string =>
  labelFor(member.metadata?.HowTheyHeard, {
    ...HOW_THEY_HEARD_FALLBACK_LABELS,
    ...Object.fromEntries((options ?? []).map((o) => [o.value, o.label])),
  });

/** Interests are stored as a list; the CRM shows them comma-separated. */
export function interestsLabel(member: MemberProfile): string {
  const ids = member.metadata?.Interests;
  if (!Array.isArray(ids)) return "";
  return ids.filter(Boolean).join(", ");
}

/**
 * A phone number as a dialable `tel:` URI, or null when there's nothing to dial.
 *
 * Percent-encoding is the wrong tool here: `tel:07000%20000000` is a valid URI that some
 * clients still won't dial. RFC 3966 wants the digits with separators removed, so strip
 * everything that isn't a digit — keeping a single leading `+` for international numbers.
 * The number is still *displayed* as the club typed it; only the href is normalised.
 */
export function telHref(phone: string | null | undefined): string | null {
  const raw = phone?.trim();
  if (!raw) return null;
  const international = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;
  return `tel:${international ? "+" : ""}${digits}`;
}
